import React, { useCallback, useEffect, useState, useContext, useRef } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";

import { Dismiss24Regular, ArrowCircleUp20Filled, MicFilled } from "@fluentui/react-icons";
import { ChatMessageGroup, ChatMessageList } from "./FluentChatOverlay";
import { MAX_MESSAGE_LENGTH } from "../../utils/chat-message";
import { PrivateChatContext } from "../chat/PrivateChatContext";

import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Persona,
  Textarea,
  Tooltip,
  makeStyles
} from "@fluentui/react-components";
import { PROVIDERS } from "../../utils/openAI-utils";
import { AudioSpeaker } from "../room/AudioSpeakerAI";
import { AudioRecorder } from "../room/AudioRecordingAI";
import { AudioRecorderUser } from "../room/AudioRecordingUserAI";
import { ReactComponent as Voices } from "../icons/voices.svg";
import { AudioSphereAIListeningStatus } from "../room/AudioTalkingSphereAIUser";
import { useMicrophoneStatus } from "../room/useMicrophoneStatus";
import { PermissionStatus } from "../../utils/media-devices-utils";
import aiAgents from "../../singleton/AIAgents";
import { AGENT_EVENTS } from "../../utils/agent-utils";
import { useVAD } from "../chat/AIChatContext";

const useStyles = makeStyles({
  headerDrawer: {
    display: " flex",
    gap: " 8px",
    alignSelf: " stretch",
    backgroundColor: "#16181E"
  },
  drawerBody: {
    paddingTop: "20px"
  },
  sectionHeader: {
    display: " flex",
    padding: " 8px 0px",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "var(--Font-size-100, 10px)",
    alignSelf: "stretch"
  },
  sectionHeaderText: {
    color: "var(--Gray-3, #828282)"
  },
  sectionContent: {
    width: "100%",
    display: " flex",
    padding: " 4px",
    alignItems: " center",
    height: "48px",
    borderRadius: "4px",
    justifyContent: "space-between",
    gap: " 8px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)"
    }
  },
  persona: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "& .fui-Persona": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    },
    "& span": {
      fontSize: "var(--Font-size-500, 20px)",
      fontStyle: "normal",
      fontWeight: "600",
      lineHeight: "var(--Line-height-500, 28px)"
    }
  },
  icons: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "& > *": {
      cursor: "pointer"
    }
  },
  divider: {
    padding: "5px 0px 4px 0px"
  },
  contentParticipants: {},
  footerParticipants: {
    display: "flex",
    padding: "16px var(--Font-size-600, 24px) var(--Font-size-600, 24px) var(--Font-size-600, 24px)",
    justifyContent: "flex-end",
    width: "100%",
    backgroundColor: "#16181E"
  },
  redIcon: {
    color: "var(--Red, #EB5757)"
  },
  messageListContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "15px"
  },
  messageBubble: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "5px",
    paddingLeft: "40px",
    "& .message-author-container": {
      justifyContent: "flex-end",
      alignItems: "center",
      gap: "10px",
      display: "inline-flex"
    },
    "& .message-bubble-content": {
      display: "flex",
      padding: "20px 15px",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      alignSelf: "stretch",
      borderRadius: "20px 0px 20px 20px",
      background: "var(--Dark, #3A4048)",
      maxWidth: "280px",
      marginLeft: "auto"
    }
  },
  messageBubbleUser: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "5px",
    "& .message-author-container": {
      justifyContent: "flex-start",
      alignItems: "center",
      gap: "10px",
      display: "inline-flex",
      flexDirection: "row-reverse"
    },
    "& .message-bubble-content": {
      display: "flex",
      padding: "20px 15px",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      alignSelf: "stretch",
      borderRadius: "0px 20px 20px 20px",
      background: "#181A20",
      maxWidth: "280px",
      marginRight: "auto"
    },
    "& .spawn-message-container": {
      flexDirection: "row-reverse"
    }
  },
  messageAuthorDate: {
    textAlign: "right",
    color: "#BDBDBD",
    fontSize: 14,
    fontFamily: "Segoe UI",
    fontWeight: "400"
  },
  messageAuthor: { textAlign: "right", color: "white", fontSize: 14, fontFamily: "Segoe UI", fontWeight: "700" },
  messageBubbleText: {
    color: "var(--White, #FFF)",
    textAlign: "right"
  },
  sendStyles: {
    backgroundColor: "#27AE60",
    "&:hover": {
      backgroundColor: "#27AE60"
    },
    minWidth: "32px",
    width: "32px",
    minHeight: "32px",
    height: "32px"
  },
  sendStylesDisabled: {
    backgroundColor: "#F0F0F0",
    minWidth: "32px",
    width: "32px",
    minHeight: "32px",
    height: "32px",
    margin: "auto 0",
    "&: hover": {
      backgroundColor: "#F0F0F0"
    }
  },
  textareaInput: {
    "& > :first-child": {
      resize: "none",
      minHeight: "32px"
    }
  },
  sendMessageContainer: {
    display: "flex",
    gap: "8px",
    width: "100%",
    justifyContent: "space-between"
  },
  inputIcon: {
    minWidth: "20px",
    width: "20px"
  },
  sendAIButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    "&: hover": {
      backgroundColor: "#fff"
    },
    width: "20px",
    height: "20px",
    borderRadius: "100%"
  },
  chatMessageList: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    paddingBottom: "60px"
  }
});

export default function FluentChatAI({
  scene,
  hubChannel,
  mySessionId,
  micActive,
  setMicActive,
  selectedUser,
  toggleChat,
  isOpenParticipants
}) {
  const intl = useIntl();
  const { privateMessageGroups, receiver, setReceiver, cancelAllPlay, sendAIPrivateMessage, sendingAIMessage } =
    useContext(PrivateChatContext);
  const [message, setMessage] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [showMessageList, setShowMessageList] = useState(true);
  const [idleSpeak, setIdleSpeak] = useState(false);
  const [loadingSpeech, setLoadingSpeech] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const { isTalking, setIsTalking, voiceMode, setVoiceMode, chatRecording, setChatRecording } = useVAD();

  const agent = aiAgents.getAgentById(receiver);

  const admitVoiceTranscription =
    agent &&
    [PROVIDERS.OPEN_AI_COMPLETION, PROVIDERS.OPEN_AI_ASSISTANT].includes(agent.provider) &&
    agent.voiceSpeechStrategy !== "Silent";

  const toggleMicActive = () => {
    setMicActive(!micActive);
  };

  const inputRef = useRef();

  const canSpawnMessages = hubChannel.can("spawn_and_move_media");

  useEffect(() => {
    setReceiver(selectedUser.id);
    const agents = aiAgents.getAgents();
    const entity = agents.find(agent => agent.id === selectedUser.id)?.el;
    if (entity.components["ai-agent"]?.isTalking) {
      setIsTalking(true);
    } else {
      setIsTalking(false);
    }
  }, [setIsTalking, setReceiver, selectedUser?.id]);

  const styles = useStyles();

  const onShowMessageList = useCallback(() => {
    setShowMessageList(true);
  }, [setShowMessageList]);

  const onSendMessage = useCallback(() => {
    sendAIPrivateMessage(message, receiver, mySessionId);
    setMessage("");

    onShowMessageList();
  }, [message, setMessage, receiver, sendAIPrivateMessage, mySessionId, onShowMessageList]);

  const onListening = useCallback(
    isListening => {
      setIsListening(isListening);
    },
    [setIsListening]
  );

  const onTalking = useCallback(
    isTalking => {
      setIsTalking(isTalking);
    },
    [setIsTalking]
  );

  const onLoadingSpeech = useCallback(
    isLoadingSpeech => {
      setLoadingSpeech(isLoadingSpeech);
    },
    [setLoadingSpeech]
  );

  const onLoadingImage = useCallback(
    loading => {
      setLoadingImage(loading);
    },
    [setLoadingImage]
  );

  const swapToKeyboard = useCallback(() => {
    setVoiceMode(false);
    setShowMessageList(true);
  }, [setVoiceMode, setShowMessageList]);

  const toggleIddleSpeak = useCallback(
    value => {
      setIdleSpeak(value);
    },
    [setIdleSpeak]
  );

  const playAudio = useCallback(
    (group, message) => {
      aiAgents.playAgentAudio(receiver, group, message);
    },
    [receiver]
  );

  const swapToVoice = useCallback(() => {
    setVoiceMode(true);
    setShowMessageList(false);
  }, [setVoiceMode, setShowMessageList]);

  const aiLoadingSpeech = useCallback(() => {
    setLoadingSpeech(true);
  }, [setLoadingSpeech]);

  const aiLoadedSpeech = useCallback(() => {
    setLoadingSpeech(false);
  }, [setLoadingSpeech]);

  const onStartRecording = useCallback(
    recording => {
      setShowMessageList(true);
      setChatRecording(recording);
    },
    [setShowMessageList, setChatRecording]
  );

  const idleTimeoutRef = useRef(null);

  const onAiProximity = useCallback(() => {
    // stopRecording();
    swapToKeyboard();
  }, [swapToKeyboard]);

  const onAiFinish = useCallback(() => {
    setIsTalking(false);
  }, [setIsTalking]);

  const onSpawn = useCallback(() => {
    // window.APP.scene.emit("add_media", { src });
  }, []);

  useEffect(() => {
    const currentAgent = receiver;

    return () => {
      cancelAllPlay(currentAgent);
    };
  }, [receiver, cancelAllPlay]);

  useEffect(() => {
    const handleAiSpeak = () => onTalking(true);

    window.addEventListener(AGENT_EVENTS.AI_SPEAK, handleAiSpeak);
    window.addEventListener(AGENT_EVENTS.AI_ON_ENTER_PROXIMITY, onAiProximity);
    window.addEventListener(AGENT_EVENTS.AI_THINKING, aiLoadingSpeech);
    window.addEventListener(AGENT_EVENTS.AI_FINISH_THINKING, aiLoadedSpeech);
    window.addEventListener(AGENT_EVENTS.AI_FINISH_SPEAK, onAiFinish);

    return () => {
      window.removeEventListener(AGENT_EVENTS.AI_SPEAK, handleAiSpeak);
      window.removeEventListener(AGENT_EVENTS.AI_FINISH_SPEAK, onAiFinish);
      window.removeEventListener(AGENT_EVENTS.AI_ON_ENTER_PROXIMITY, onAiProximity);
      window.removeEventListener(AGENT_EVENTS.AI_THINKING, aiLoadingSpeech);
      window.removeEventListener(AGENT_EVENTS.AI_FINISH_THINKING, aiLoadedSpeech);
    };
  }, [onAiFinish, onAiProximity, onTalking, aiLoadingSpeech, aiLoadedSpeech]);

  useEffect(() => {
    if (isTalking) {
      aiAgents.status = "talking";
    }

    if (chatRecording) {
      aiAgents.status = "listening";
    }

    if (loadingSpeech) {
      aiAgents.status = "thinking";
    }

    if (!isTalking && !chatRecording && !loadingSpeech) {
      aiAgents.status = "idle";
    }
  }, [isTalking, chatRecording, loadingSpeech]);

  useEffect(() => {
    const handleAiEndAudio = () => onTalking(false);
    window?.addEventListener("ai-end-audio", handleAiEndAudio);
    return () => {
      window?.removeEventListener("ai-end-audio", handleAiEndAudio);
    };
  }, [onTalking]);

  useEffect(() => {
    return () => {
      setVoiceMode(false);
    };
  }, [setVoiceMode]);

  useEffect(() => {
    if (voiceMode) {
      idleTimeoutRef.current = setTimeout(() => {
        toggleIddleSpeak(true);
      }, 8000); // 10 seconds
    } else {
      clearTimeout(idleTimeoutRef.current);
      toggleIddleSpeak(false);
    }

    return () => {
      clearTimeout(idleTimeoutRef.current);
    };
  }, [voiceMode, toggleIddleSpeak]);

  const onResetIdle = () => {
    setIdleSpeak(false);
    clearTimeout(idleTimeoutRef.current);
    idleTimeoutRef.current = setTimeout(() => {
      toggleIddleSpeak(true);
    }, 8000);
  };

  const aiPrivateMessages = privateMessageGroups.filter(group => group.receiver === receiver);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (message.trim()) {
          onSendMessage();
        }
      }
    },
    [message, onSendMessage]
  );

  return (
    <>
      <DrawerHeader className={styles.headerDrawer}>
        <DrawerHeaderTitle
          style={{ width: "100%" }}
          action={
            <Button
              appearance="subtle"
              aria-label={intl.formatMessage({ id: "close-button", defaultMessage: "Close" })}
              icon={<Dismiss24Regular />}
              onClick={() => toggleChat(false)}
            />
          }
        >
          {/* {selectedUser?.name} */}
          <Persona
            size="large"
            className={styles.persona}
            name={`${selectedUser?.name}`}
            avatar={{
              color: "colorful"
            }}
          />
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={styles.drawerBody}>
        {showMessageList && (
          <ChatMessageList className={styles.chatMessageList}>
            {receiver &&
              aiPrivateMessages.map(entry => {
                const { id } = entry;
                return (
                  <ChatMessageGroup
                    key={id}
                    {...entry}
                    playAudio={message => playAudio(entry, message)}
                    cancelAllPlay={cancelAllPlay}
                    onSpawn={onSpawn}
                    canSpawnMessages={canSpawnMessages}
                  />
                );
              })}
          </ChatMessageList>
        )}
        {!showMessageList && receiver && (
          <>
            {isTalking || isListening || loadingSpeech ? (
              <AITalkingSphere
                loadingSpeech={loadingSpeech}
                onResetIdle={onResetIdle}
                onTalking={onTalking}
                talking={isTalking}
                scene={scene}
                listening={isListening}
                loadingImage={loadingImage}
                idleSpeak={idleSpeak}
                onLoadingSpeech={onLoadingSpeech}
                isOpenParticipants={isOpenParticipants}
              />
            ) : (
              <AIListeningSphere
                micActive={micActive}
                loadingSpeech={loadingSpeech}
                onResetIdle={onResetIdle}
                onTalking={onTalking}
                talking={isTalking}
                scene={scene}
                listening={isListening}
                loadingImage={loadingImage}
                idleSpeak={idleSpeak}
                isOpenParticipants={isOpenParticipants}
              />
            )}
            <AudioRecorderUser
              textMode={!voiceMode}
              setLoadingSpeech={setLoadingSpeech}
              onResetIdle={onResetIdle}
              toggleIddleSpeak={toggleIddleSpeak}
              swapToKeyboard={swapToKeyboard}
              talking={isTalking}
              onTalking={onTalking}
              onListening={onListening}
              scene={scene}
              receiver={receiver}
              onLoadingImage={onLoadingImage}
              mySessionId={mySessionId}
              toggleMicActive={toggleMicActive}
              micActive={micActive}
              loadingSpeech={loadingSpeech}
            ></AudioRecorderUser>
          </>
        )}
      </DrawerBody>

      {voiceMode ? (
        <></>
      ) : (
        <div className={styles.footerParticipants}>
          <AudioRecorder
            setMessage={setMessage}
            styles={styles}
            onResetIdle={onResetIdle}
            mySessionId={mySessionId}
            scene={scene}
            receiver={receiver}
            recording={chatRecording}
            setRecording={setChatRecording}
          ></AudioRecorder>
          {!chatRecording && (
            <div className={styles.sendMessageContainer}>
              <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                <Textarea
                  ref={inputRef}
                  onChange={e => setMessage(e.target.value)}
                  value={message}
                  maxLength={MAX_MESSAGE_LENGTH}
                  appearance="filled-ligther"
                  placeholder="Type message here"
                  className={styles.textareaInput}
                  style={{ display: "flex", flex: 1 }}
                  onKeyDown={handleKeyDown}
                />
                <div style={{ display: "flex", gap: "4px" }}>
                  {!message && admitVoiceTranscription && (
                    <RecordButtonInput scene={scene} onClick={() => onStartRecording(true)} />
                  )}
                  {message && <SendButton disabled={sendingAIMessage} styles={styles} onClick={onSendMessage} />}
                </div>
              </div>
              {!message && admitVoiceTranscription && (
                <Button onClick={swapToVoice} className={styles.sendStylesDisabled} icon={<Voices />} />
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

function RecordButtonInput({ onClick, scene }) {
  const { permissionStatus } = useMicrophoneStatus(scene);
  const micPermissionDenied = permissionStatus === PermissionStatus.DENIED;
  const [visible, setVisible] = React.useState(false);

  return (
    <Tooltip
      visible={visible && micPermissionDenied}
      onVisibleChange={(_ev, data) => setVisible(data.visible)}
      content={"Your microphone is denied by your browser"}
    >
      <Button disabled={micPermissionDenied} appearance="transparent" onClick={onClick} icon={<MicFilled />} />
    </Tooltip>
  );
}

RecordButtonInput.propTypes = {
  onClick: PropTypes.func,
  scene: PropTypes.object
};

function SendButton({ onClick, disabled }) {
  const handleonClick = () => {
    if (!disabled) {
      onClick();
    }
  };

  const buttonStyles = !disabled
    ? { color: "#fff", cursor: "pointer" }
    : { color: "var(--colorNeutralForegroundDisabled, #F0F0F0)" };
  const [visible, setVisible] = React.useState(false);

  return (
    <Tooltip
      visible={visible && disabled}
      onVisibleChange={(_ev, data) => setVisible(data.visible)}
      content={"The Agent is currently processing your prompt"}
    >
      <ArrowCircleUp20Filled style={buttonStyles} onClick={handleonClick} />
    </Tooltip>
  );
}

SendButton.propTypes = {
  onClick: PropTypes.func,
  disabled: PropTypes.bool
};

function AIListeningSphere({ scene, talking, onTalking, onResetIdle, loadingSpeech, onLoadingSpeech, ...rest }) {
  const { receiver } = useContext(PrivateChatContext);
  return (
    <>
      <AudioSphereAIListeningStatus
        loadingSpeech={loadingSpeech}
        talking={talking}
        onResetIdle={onResetIdle}
        onTalking={onTalking}
        scene={scene}
        receiver={receiver}
        onLoadingSpeech={onLoadingSpeech}
        {...rest}
      ></AudioSphereAIListeningStatus>
    </>
  );
}

function AITalkingSphere({ scene, talking, onTalking, onResetIdle, loadingSpeech, onLoadingSpeech }) {
  const { receiver } = useContext(PrivateChatContext);
  return (
    <AudioSpeaker
      loadingSpeech={loadingSpeech}
      talking={talking}
      onResetIdle={onResetIdle}
      onTalking={onTalking}
      scene={scene}
      receiver={receiver}
      onLoadingSpeech={onLoadingSpeech}
    ></AudioSpeaker>
  );
}

AIListeningSphere.propTypes = {
  micActive: PropTypes.bool,
  loadingSpeech: PropTypes.bool,
  onResetIdle: PropTypes.func,
  onTalking: PropTypes.func,
  talking: PropTypes.bool,
  scene: PropTypes.object,
  listening: PropTypes.bool,
  loadingImage: PropTypes.bool,
  idleSpeak: PropTypes.bool,
  onLoadingSpeech: PropTypes.func,
  isOpenParticipants: PropTypes.bool
};

FluentChatAI.propTypes = {
  scene: PropTypes.object.isRequired,
  hubChannel: PropTypes.shape({
    can: PropTypes.func.isRequired
  }).isRequired,
  mySessionId: PropTypes.string.isRequired,
  micActive: PropTypes.bool.isRequired,
  setMicActive: PropTypes.func.isRequired,
  selectedUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired,
  toggleChat: PropTypes.func.isRequired,
  isOpenParticipants: PropTypes.bool.isRequired
};

AITalkingSphere.propTypes = {
  scene: PropTypes.object,
  talking: PropTypes.bool,
  onTalking: PropTypes.func,
  onResetIdle: PropTypes.func,
  loadingSpeech: PropTypes.bool,
  onLoadingSpeech: PropTypes.func
};
